import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimerIcon from '@mui/icons-material/Timer';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { getRelationDisplay, RELATIONS } from '../../third_parties/map/placeAutoComplete';
import SpotAddressLine from './SpotAddressLine';
import SpotReviewComposerModal from './SpotReviewComposer/SpotReviewComposerModal';

// Pure, props-only renderer for a single spot review in the "detail page"
// layout. Used both by SpotReviewDetail (with fetched data) and by the
// preview step of the standalone review composer. Keeping this component
// free of routing / fetching lets the two surfaces stay in lockstep.
//
// Props:
//   review         — server-shape review object (with photos, spot_name,
//                    spot_address, spot_place_id, visit_*, price, etc.)
//   subReviews     — list of sub-reviews to show under "Related spots".
//                    When undefined, falls back to review.sub_reviews.
//   onSubClick     — (sub) => void; how to handle sub-card clicks.
//                    Defaults to no-op so the component is navigation-free
//                    by default — the detail page injects routing, the
//                    preview step can pass undefined.
//   allowAddRelated   — when provided, shows parking/viewpoint/trailhead
//                    pills that open SpotReviewComposerModal. Gated by the
//                    caller (owner-only, composer preview only).
//   addRelatedDraftSubmit — when provided, the pills run in draft mode:
//                    each pill's composer uses onDraftSubmit rather than
//                    posting. Used by the standalone-creation preview step
//                    where the parent itself hasn't been POSTed yet, so
//                    subs must also stay local until the final Done.
const SpotReviewPreview = ({
    review,
    subReviews,
    onSubClick,
    allowAddRelated,
    addRelatedDraftSubmit,
    isOwner,
    onEdit,
    onDelete,
}) => {
    const [photoIdx, setPhotoIdx] = useState(0);
    const [menuAnchor, setMenuAnchor] = useState(null);

    if (!review) return null;

    const {
        title,
        content,
        owner_first_name,
        owner_last_name,
        avatar: reviewAvatar,
        photos,
        visit_start_time,
        visitStartTime,
        visit_duration,
        visitDuration,
        visit_from,
        visit_to,
        price,
        spot_name,
        spot_address,
        spot_place_id,
        id: reviewId,
    } = review;

    const subs = subReviews != null ? subReviews : (review.sub_reviews || []);

    const startTime =
        visit_start_time ||
        visitStartTime ||
        (visit_from ? new Date(visit_from * 1000).toISOString() : null);
    const duration =
        visit_duration ||
        visitDuration ||
        (visit_from && visit_to && visit_to > visit_from
            ? Math.round((visit_to - visit_from) / 60)
            : null);

    const formatStartTime = (t) => {
        if (!t) return null;
        const d = new Date(t);
        if (isNaN(d.getTime())) return null;
        return d.toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    };
    const formatDuration = (mins) => {
        if (!mins) return null;
        const m = parseInt(mins);
        if (m >= 1440) return 'Full day';
        if (m >= 60) return m % 60 === 0 ? `${m / 60}h` : `${Math.floor(m / 60)}h ${m % 60}m`;
        return `${m}m`;
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: 'flex', gap: 4, flexDirection: { xs: 'column', md: 'row' } }}>
                {/* Left: photo gallery with thumbnails */}
                {photos && photos.length > 0 && (
                    <Box sx={{ flex: '0 0 45%', maxWidth: { md: '45%' } }}>
                        <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden', mb: 1.5 }}>
                            <img
                                src={photos[photoIdx]?.image_link}
                                alt=""
                                style={{ width: '100%', height: 'auto', display: 'block' }}
                            />
                            {photos.length > 1 && (
                                <>
                                    <IconButton
                                        onClick={() => setPhotoIdx((photoIdx - 1 + photos.length) % photos.length)}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            left: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            bgcolor: 'rgba(0,0,0,0.35)',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                                        }}
                                    >
                                        <ChevronLeftIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                        onClick={() => setPhotoIdx((photoIdx + 1) % photos.length)}
                                        size="small"
                                        sx={{
                                            position: 'absolute',
                                            right: 8,
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            bgcolor: 'rgba(0,0,0,0.35)',
                                            color: 'white',
                                            '&:hover': { bgcolor: 'rgba(0,0,0,0.55)' },
                                        }}
                                    >
                                        <ChevronRightIcon fontSize="small" />
                                    </IconButton>
                                    <Box sx={{ position: 'absolute', bottom: 8, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 0.75 }}>
                                        {photos.map((_, i) => (
                                            <Box
                                                key={i}
                                                onClick={() => setPhotoIdx(i)}
                                                sx={{
                                                    width: 7,
                                                    height: 7,
                                                    borderRadius: '50%',
                                                    cursor: 'pointer',
                                                    bgcolor: i === photoIdx ? 'white' : 'rgba(255,255,255,0.5)',
                                                    transition: 'background-color 200ms',
                                                }}
                                            />
                                        ))}
                                    </Box>
                                </>
                            )}
                        </Box>
                        {photos.length > 1 && (
                            <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 0.5 }}>
                                {photos.map((p, i) => (
                                    <Box
                                        key={i}
                                        onClick={() => setPhotoIdx(i)}
                                        sx={{
                                            flex: '0 0 56px',
                                            width: 56,
                                            height: 42,
                                            borderRadius: 1,
                                            overflow: 'hidden',
                                            cursor: 'pointer',
                                            opacity: i === photoIdx ? 1 : 0.5,
                                            border: '2px solid',
                                            borderColor: i === photoIdx ? 'primary.main' : 'transparent',
                                            transition: 'opacity 200ms, border-color 200ms',
                                            '&:hover': { opacity: 1 },
                                        }}
                                    >
                                        {p && p.image_link && (
                                            <img
                                                src={p.image_link}
                                                alt=""
                                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                )}

                {/* Right: title, author, address, content, price */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            {title}
                        </Typography>
                        {isOwner && (onEdit || onDelete) && (
                            <>
                                <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)}>
                                    <MoreVertIcon />
                                </IconButton>
                                <Menu
                                    anchorEl={menuAnchor}
                                    open={Boolean(menuAnchor)}
                                    onClose={() => setMenuAnchor(null)}
                                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                                >
                                    {onEdit && (
                                        <MenuItem onClick={() => { setMenuAnchor(null); onEdit(); }}>
                                            <ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>
                                            <ListItemText>Edit</ListItemText>
                                        </MenuItem>
                                    )}
                                    {onDelete && (
                                        <MenuItem onClick={() => { setMenuAnchor(null); onDelete(); }}>
                                            <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                                            <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
                                        </MenuItem>
                                    )}
                                </Menu>
                            </>
                        )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        {reviewAvatar && (
                            <Avatar src={reviewAvatar.image_link} sx={{ width: 24, height: 24 }} />
                        )}
                        {(review.owner_name || owner_first_name) && (
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                {review.owner_name || (owner_first_name + ' ' + (owner_last_name || ''))}
                            </Typography>
                        )}
                        {startTime && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                                <AccessTimeIcon sx={{ fontSize: 13 }} />
                                {formatStartTime(startTime)}
                            </Typography>
                        )}
                        {duration && (
                            <Typography variant="caption" sx={{ color: 'text.disabled', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TimerIcon sx={{ fontSize: 13 }} />
                                {formatDuration(duration)}
                            </Typography>
                        )}
                    </Box>

                    {(spot_name || spot_address) && (
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
                                <SpotAddressLine
                                    placeName={spot_name}
                                    placeAddress={spot_address}
                                    placeId={spot_place_id}
                                />
                            </Typography>
                        </Box>
                    )}

                    {content && (
                        <Typography variant="body1" sx={{ lineHeight: 1.8, color: 'text.primary', whiteSpace: 'pre-wrap' }}>
                            {content}
                        </Typography>
                    )}

                    {!!price && Number(price) !== 0 && (
                        <Typography variant="body2" sx={{ color: 'text.disabled', mt: 3 }}>
                            Estimated price per person: ${price}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Related section. Always rendered when we have subs or when the
                caller opted into add-related (preview step / owner view). */}
            {(subs.length > 0 || allowAddRelated) && (
                <Box sx={{ mt: 6 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Related spots
                    </Typography>
                    {subs.length > 0 && (
                        <Stack spacing={1.5} sx={{ mb: allowAddRelated ? 2 : 0 }}>
                            {subs.map((sub) => {
                                const rd = getRelationDisplay(sub.relation);
                                const photo = sub.photos && sub.photos[0] && sub.photos[0].image_link;
                                return (
                                    <Card key={sub.id} variant="outlined" sx={{ borderRadius: 2 }}>
                                        <CardActionArea
                                            onClick={() => onSubClick && onSubClick(sub)}
                                            disabled={!onSubClick}
                                            sx={{ display: 'flex', alignItems: 'stretch' }}
                                        >
                                            {photo && (
                                                <Box
                                                    sx={{
                                                        width: 96,
                                                        minWidth: 96,
                                                        height: 96,
                                                        backgroundImage: `url(${photo})`,
                                                        backgroundSize: 'cover',
                                                        backgroundPosition: 'center',
                                                    }}
                                                />
                                            )}
                                            <CardContent sx={{ flex: 1, py: 1.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    {rd && (
                                                        <Chip
                                                            size="small"
                                                            icon={rd.icon}
                                                            label={rd.label}
                                                            sx={{ fontWeight: 500 }}
                                                        />
                                                    )}
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                        {sub.title}
                                                    </Typography>
                                                </Box>
                                                {(sub.spot_name || sub.spot_address) && (
                                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                                        <SpotAddressLine
                                                            placeName={sub.spot_name}
                                                            placeAddress={sub.spot_address}
                                                            placeId={sub.spot_place_id}
                                                        />
                                                    </Typography>
                                                )}
                                            </CardContent>
                                        </CardActionArea>
                                    </Card>
                                );
                            })}
                        </Stack>
                    )}

                    {/* Pill triggers for adding a related spot. Two modes:
                        - Saved mode (reviewId present): opens the lite composer
                          pre-wired with parentId + relation — same code path
                          as TripSpotItem, so BE stays on one sub-review flow.
                        - Draft mode (addRelatedDraftSubmit present): the parent
                          isn't POSTed yet. Use onDraftSubmit so the nested
                          composer hands the draft back; the caller stashes it
                          and will POST it after the parent on Done. */}
                    {allowAddRelated && (reviewId || addRelatedDraftSubmit) && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(RELATIONS).map(([key, rd]) => (
                                <SpotReviewComposerModal
                                    key={key}
                                    parentId={addRelatedDraftSubmit ? undefined : reviewId}
                                    relation={key}
                                    onDraftSubmit={
                                        addRelatedDraftSubmit
                                            ? (sub) => addRelatedDraftSubmit({ ...sub, relation: key })
                                            : undefined
                                    }
                                    triggerLabel={`+ ${rd.label}`}
                                    triggerSx={{
                                        textTransform: 'none',
                                        borderRadius: 999,
                                        borderStyle: 'dashed',
                                        borderWidth: 1,
                                        px: 1.5,
                                        py: 0.5,
                                        minHeight: 0,
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                        color: 'primary.main',
                                        borderColor: 'primary.light',
                                    }}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SpotReviewPreview;
